import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './DiscordDashboard.css';
import '../Zitadel-Auth/Auth.css';

interface GuildSettings {
  guild_id: string;
  guild_name?: string;
  command_channels: string[];
  spawn_updates_enabled: boolean;
  spawn_update_channels: string[];
  created_at: string;
  updated_at: string;
}

interface DiscordChannel {
  id: string;
  name: string;
  type: number;
}

interface DiscordGuild {
  id: string;
  name: string;
  icon: string | null;
  owner: boolean;
  permissions: string;
  channels?: DiscordChannel[];
}

const DiscordServerSettings: React.FC = () => {
  const navigate = useNavigate();
  const { guildId } = useParams<{ guildId: string }>();
  const [guild, setGuild] = useState<DiscordGuild | null>(null);
  const [settings, setSettings] = useState<GuildSettings | null>(null);
  const [channels, setChannels] = useState<DiscordChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const discordToken = localStorage.getItem('discordToken');

    if (!discordToken) {
      navigate('/dashboard');
      return;
    }

    if (!guildId) {
      navigate('/dashboard');
      return;
    }

    fetchGuildData();
  }, [guildId, navigate]);

  const fetchGuildData = async () => {
    try {
      setLoading(true);

      const discordToken = localStorage.getItem('discordToken');

      // Fetch guild info and channels
      const [guildResponse, settingsResponse] = await Promise.all([
        fetch(`/api/discord/guilds/${guildId}/channels`, {
          headers: {
            'Authorization': `Bearer ${discordToken}`
          }
        }),
        fetch(`/api/discord/settings/${guildId}`, {
          headers: {
            'Authorization': `Bearer ${discordToken}`
          }
        })
      ]);

      if (!guildResponse.ok) {
        if (guildResponse.status === 401) {
          // Discord token is invalid or expired
          localStorage.removeItem('discordToken');
          navigate('/dashboard');
          return;
        }
        throw new Error('Failed to fetch guild data');
      }

      const guildData = await guildResponse.json();
      setGuild(guildData.guild);
      setChannels(guildData.channels.filter((ch: DiscordChannel) => ch.type === 0)); // Text channels only

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        setSettings(settingsData);
      } else {
        // No settings exist yet, create default
        setSettings({
          guild_id: guildId!,
          guild_name: guildData.guild.name,
          command_channels: [],
          spawn_updates_enabled: false,
          spawn_update_channels: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error fetching guild data:', error);
      setError('Failed to load server information');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<GuildSettings>) => {
    if (!settings) return;

    try {
      setSaving(true);
      const response = await fetch(`/api/discord/settings/${guildId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('discordToken')}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      const updatedSettings = await response.json();
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update server settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCommandChannelsChange = (channelIds: string[]) => {
    updateSettings({ command_channels: channelIds });
  };

  const handleSpawnUpdateChannelChange = (channelId: string) => {
    updateSettings({
      spawn_update_channels: channelId ? [channelId] : []
    });
  };

  const handleSpawnUpdatesToggle = (enabled: boolean) => {
    const updates: Partial<GuildSettings> = { spawn_updates_enabled: enabled };
    if (!enabled) {
      updates.spawn_update_channels = [];
    }
    updateSettings(updates);
  };

  // If no Discord token, redirect to dashboard
  const discordToken = localStorage.getItem('discordToken');
  if (!discordToken) {
    navigate('/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className="discord-dashboard-container">
        <div className="discord-dashboard">
          <div className="dashboard-header">
            <h1>⚙️ Server Settings</h1>
            <button onClick={() => navigate('/dashboard')} className="back-button">
              ← Back to Dashboard
            </button>
          </div>
          <div className="loading">Loading server settings...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="discord-dashboard-container">
        <div className="discord-dashboard">
          <div className="dashboard-header">
            <h1>⚙️ Server Settings</h1>
            <button onClick={() => navigate('/dashboard')} className="back-button">
              ← Back to Dashboard
            </button>
          </div>
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="discord-dashboard-container">
      <div className="discord-dashboard">
        <div className="dashboard-header">
          <h1>⚙️ {guild?.name} Settings</h1>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            ← Back to Dashboard
          </button>
        </div>

        <div className="dashboard-content">
          <div className="server-info">
            <div className="server-header">
              {guild?.icon && (
                <img
                  src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                  alt={`${guild.name} icon`}
                  className="server-icon"
                />
              )}
              <div>
                <h2>{guild?.name}</h2>
                <span className="server-id">Server ID: {guildId}</span>
              </div>
            </div>
          </div>

          <div className="settings-form">
            <div className="setting-section">
              <h3>📝 Command Channels</h3>
              <p className="setting-description">
                Select which channels the bot can respond to commands in. Leave empty to allow all channels.
              </p>

              <div className="channel-selector">
                <div className="selected-channels">
                  {settings?.command_channels?.map(channelId => {
                    const channel = channels.find(ch => ch.id === channelId);
                    return channel ? (
                      <span key={channelId} className="channel-tag">
                        #{channel.name}
                        <button
                          onClick={() => {
                            const newChannels = settings.command_channels.filter(id => id !== channelId);
                            handleCommandChannelsChange(newChannels);
                          }}
                          className="remove-channel"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>

                <select
                  onChange={(e) => {
                    if (e.target.value && !settings?.command_channels?.includes(e.target.value)) {
                      handleCommandChannelsChange([...(settings?.command_channels || []), e.target.value]);
                    }
                    e.target.value = '';
                  }}
                  value=""
                  className="channel-dropdown"
                >
                  <option value="">Add a channel...</option>
                  {channels
                    .filter(ch => !settings?.command_channels?.includes(ch.id))
                    .map(channel => (
                      <option key={channel.id} value={channel.id}>
                        #{channel.name}
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>

            <div className="setting-section">
              <h3>🔄 Spawn Data Updates</h3>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={settings?.spawn_updates_enabled || false}
                  onChange={(e) => handleSpawnUpdatesToggle(e.target.checked)}
                  disabled={saving}
                />
                <span className="toggle-text">Enable spawn data update notifications</span>
              </label>

              {settings?.spawn_updates_enabled && (
                <div className="spawn-channel-selector">
                  <p className="setting-description">
                    Select the channel where spawn update notifications will be sent.
                  </p>
                  <select
                    value={settings.spawn_update_channels?.[0] || ''}
                    onChange={(e) => handleSpawnUpdateChannelChange(e.target.value)}
                    className="channel-dropdown"
                    disabled={saving}
                  >
                    <option value="">Select a channel...</option>
                    {channels.map(channel => (
                      <option key={channel.id} value={channel.id}>
                        #{channel.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {saving && (
              <div className="saving-indicator">
                Saving changes...
              </div>
            )}

            {settings && (
              <div className="settings-info">
                <small>Last updated: {new Date(settings.updated_at).toLocaleString()}</small>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscordServerSettings;
